import React, { useCallback, useContext, useMemo, useState } from "react";
import { Table, Tooltip, Icon } from "antd";
import * as R from "ramda";

import ConfigurationComponent from "./ConfigurationComponent/";
import {
  isFixedOption,
  isFixedQuestion,
  isConfigurableOption,
  questionHasError
} from "./constants";
import QuestionActions from "./QuestionActions";

import styled, { ThemeContext } from "styled-components/macro";

const TooltipContainer = styled.div`
  display: inline-flex;
  align-items: center;

  .anticon {
    margin-left: 6px;
  }
`;

const FixedOptionLabel = styled.span`
  color: ${({ enabled, theme }) => (enabled ? theme["@gray-80"] : "inherit")};
  font-weight: ${({ enabled }) => (enabled ? "500" : "inherit")};
`;

const StyledTable = styled(Table)`
  table {
    table-layout: fixed;
  }

  .ant-table-body {
    background-color: inherit;
  }

  .ant-table-thead,
  .ant-table-row {
    background-color: ${({ theme }) => theme["@gray-20"]};
    transition: background-color 0s;
  }

  .row-is-expanded,
  .row-is-expanded:hover {
    background-color: ${({ theme }) => theme["@white"]};
  }

  tr.ant-table-expanded-row,
  tr.ant-table-expanded-row:hover,
  .ant-table-expanded-row {
    background-color: ${({ theme }) => theme["@white"]};
  }

  .ant-table-thead > tr.ant-table-row-hover:not(.ant-table-expanded-row) > td,
  .ant-table-tbody > tr.ant-table-row-hover:not(.ant-table-expanded-row) > td,
  .ant-table-thead > tr:hover:not(.ant-table-expanded-row) > td,
  .ant-table-tbody > tr:hover:not(.ant-table-expanded-row) > td {
    /* background-color: inherit; */
    background-color: ${({ theme }) => theme["@white"]};
  }
`;

const ColumnTitle = styled.span`
  font-weight: 400;
`;

function ConfigurationComponentWrapper({ option, t, updateOption }) {
  const handleError = useCallback(
    error =>
      updateOption(option, {
        error: error || false
      }),
    [option, updateOption]
  );
  const handleChange = useCallback(
    updatedOption => updateOption(option, updatedOption),
    [option, updateOption]
  );
  return (
    <ConfigurationComponent
      error={option.error}
      onChange={handleChange}
      onError={handleError}
      option={option}
      t={t}
      type={option.type}
    />
  );
}

const ScreeningTable = ({
  removeOption,
  updateOption,
  isDisabled,
  setFieldValue,
  setQuestionModal,
  t,
  editQuestionsEnabled = true,
  removeQuestionsEnabled = true,
  isEditingScreening,
  values
}) => {
  const theme = useContext(ThemeContext);
  const [expandedRows, setExpandedRows] = useState([]);

  const getRowKey = useCallback(
    record =>
      `row-${
        isFixedQuestion(record) || isFixedOption(record)
          ? record.type
          : record.index
      }`,
    []
  );

  const getRowClass = useCallback(
    record => {
      const rowKey = getRowKey(record);
      const isExpanded = R.includes(rowKey, expandedRows);
      return isExpanded ? "row-is-expanded" : "";
    },
    [expandedRows, getRowKey]
  );

  const handleRow = useCallback(
    record => ({
      "data-test": getRowKey(record)
    }),
    [getRowKey]
  );

  const screeningExpandedRowRender = useCallback(
    option => (
      <ConfigurationComponentWrapper
        t={t}
        option={option}
        updateOption={updateOption}
      />
    ),
    [t, updateOption]
  );

  const screeningLabelRender = useCallback(
    (text, record) =>
      isFixedOption(record) || isFixedQuestion(record) ? (
        <FixedOptionLabel enabled={record.enabled}>
          {text}
          {isConfigurableOption(record) && questionHasError(record) && (
            <Tooltip title={record.tooltipError}>
              <Icon
                style={{
                  marginLeft: 8,
                  fontSize: 16,
                  color: theme["@alert"]
                }}
                type="exclamation-circle"
                theme="filled"
              />
            </Tooltip>
          )}
        </FixedOptionLabel>
      ) : (
        text
      ),
    [theme]
  );

  const screeningResponseTypeRender = useCallback(
    (type, record) => (
      <span data-test={`question-type-${type}`}>
        {t(
          `opportunity:screening.${type}${
            isFixedQuestion(record) || isFixedOption(record)
              ? `Type${
                  !isConfigurableOption(record)
                    ? ""
                    : record.isEmpty &&
                      record.isEmpty(record) &&
                      !questionHasError(record)
                    ? ""
                    : questionHasError(record)
                    ? "Invalid"
                    : "Valid"
                }`
              : ""
          }`
        )}
      </span>
    ),
    [t]
  );

  const screeningLabelKnockoutRender = useCallback(
    (isKnockout, record) =>
      record.knockout
        ? t("global:yes")
        : record.knockout === false
        ? t("global:no")
        : isFixedOption(record) || isFixedQuestion(record)
        ? "-"
        : isKnockout
        ? t("global:yes")
        : t("global:no"),
    [t]
  );

  const screeningActionsRender = useCallback(
    (_, record) => (
      <QuestionActions
        editQuestionsEnabled={editQuestionsEnabled}
        expandedRows={expandedRows}
        isDisabled={isDisabled}
        getRowKey={getRowKey}
        record={record}
        removeOption={removeOption}
        removeQuestionsEnabled={removeQuestionsEnabled}
        setExpandedRows={setExpandedRows}
        setFieldValue={setFieldValue}
        setQuestionModal={setQuestionModal}
        updateOption={updateOption}
      />
    ),
    [
      editQuestionsEnabled,
      expandedRows,
      getRowKey,
      isDisabled,
      removeOption,
      removeQuestionsEnabled,
      setFieldValue,
      setQuestionModal,
      updateOption
    ]
  );

  const dataSource = useMemo(
    () =>
      [
        ...Object.keys(values.fixedOptions).map(key => ({
          text: t(`opportunity:screening.${key}`),
          formValueKey: key,
          type: key,
          enabled: values.fixedOptions[key]
        })),
        ...values.questions.map((question, index) => ({
          ...question,
          index
        }))
      ].map((question, index) => ({
        ...question,
        key: `${index}-${question.text}`
      })),
    [t, values.fixedOptions, values.questions]
  );

  const renderNull = useCallback(() => null, []);

  return (
    <StyledTable
      expandIconAsCell={false}
      expandIcon={renderNull}
      expandIconColumnIndex={3}
      rowKey={getRowKey}
      rowClassName={getRowClass}
      expandedRowKeys={isEditingScreening ? [] : expandedRows}
      expandedRowRender={screeningExpandedRowRender}
      style={{ marginBottom: 18 }}
      pagination={false}
      onRow={handleRow}
      columns={[
        {
          title: (
            <ColumnTitle>{t("opportunity:screening.screening")}</ColumnTitle>
          ),
          dataIndex: "text",
          key: "text",
          render: screeningLabelRender,
          width: "30%"
        },
        {
          title: (
            <ColumnTitle>{t("opportunity:screening.responseType")}</ColumnTitle>
          ),
          dataIndex: "type",
          key: "type",
          render: screeningResponseTypeRender,
          width: "40%"
        },
        {
          title: (
            <TooltipContainer>
              <ColumnTitle>{t("opportunity:screening.knockout")}</ColumnTitle>
              <Tooltip
                placement="top"
                title={t("opportunity:screening.knockoutTooltip")}
              >
                <Icon type="question-circle" />
              </Tooltip>
            </TooltipContainer>
          ),
          dataIndex: "knockout",
          key: "knockout",
          render: screeningLabelKnockoutRender
        },
        {
          key: "options",
          render: screeningActionsRender
        }
      ]}
      dataSource={dataSource}
    />
  );
};

export default ScreeningTable;
